import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import capitalize from 'lodash/capitalize';
import { useQuery } from '@apollo/client';
import { Text } from '@atlaskit/primitives';
import {
  emptyStateProp,
  errorStateProp,
  genericEmptyStateProp,
  TableHeading,
  CloudSiteLink,
  NoData,
  BillingProfileNameFromId,
} from '@css-xp/ppc-common';
import { getLatestPeriodEndDate } from '@css-xp/entitlements-v2';
import { routePaths, SUBSCRIPTION_CONSTANTS } from '@css-xp/ppc-constants';
import { DynamicResultTable, getRouteUrl } from '@css-xp/base';
import { CloudProductDetailView, EntitlementStatus } from '@css-xp/base/entitlement-utils';
import {
  LinkedEntitlementTypes,
  LinkedProductTypes,
  RelationshipPathType,
  Maybe,
  FetchSandboxForEntitlementForPpcQueryVariables,
  FetchSandboxForEntitlementForPpcQuery,
  BillingStatus,
} from '@css-xp/graphtypes/src/generated-types-new';
import { isFutureDatedInactiveSubscription } from '@css-xp/ppc-utils';
import { AppsCountCell as AppsCount } from '@css-xp/ppc-subscription-table';
import { AppsCountCell } from './sandbox-tab.styles';
import { fetchSandboxForEntitlementForPPC } from '../../../queries/fetch-sandbox-for-entitlement-for-ppc';
import './sandbox-tab.scss';

interface SandboxTabProps {
  id?: string;
  currentEntitlementType: LinkedEntitlementTypes;
  destinationEntitlementType: Array<LinkedEntitlementTypes>;
  productType: Maybe<LinkedProductTypes> | undefined;
  findProducts?: boolean;
  findApps?: boolean;
  entitlementId: string;
  transactionAccountId: string;
  tableHeading?: string;
  isDesignIteration1Enabled?: boolean;
  isSandboxDecouplingEnabled?: boolean;
  isFutureDatedEntitlementEnabled?: boolean;
}

export const SandboxTab = ({
  id,
  currentEntitlementType,
  destinationEntitlementType,
  productType,
  findProducts,
  findApps,
  entitlementId,
  transactionAccountId,
  tableHeading = 'Sandbox',
  isDesignIteration1Enabled,
  isSandboxDecouplingEnabled,
  isFutureDatedEntitlementEnabled,
}: SandboxTabProps) => {
  let { data, loading, error } = useQuery<
    FetchSandboxForEntitlementForPpcQuery,
    FetchSandboxForEntitlementForPpcQueryVariables
  >(fetchSandboxForEntitlementForPPC, {
    variables: {
      id: entitlementId,
      transactionAccountId,
      searchLinkedEntitlementParams: {
        entitlementId,
        containerId: id ?? null,
        currentEntitlementType,
        destinationEntitlementType,
        productType,
        findProducts,
        findApps,
      },
    },
    displayName: 'fetchSandboxForEntitlementForPPC',
    notifyOnNetworkStatusChange: true,
  });
  if (error) {
    // if  the user is having sandbox on different txa, we will not show that as error as this is expected behaviour
    error = error.message.includes('user is not a billing admin') ? undefined : error;
  }

  const subscriptionsRoutePPC = getRouteUrl(routePaths.subscriptions, { txaId: transactionAccountId });
  const version = isDesignIteration1Enabled ? 2 : undefined;

  const sandboxEdges = useMemo(() => {
    if (currentEntitlementType === LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT) {
      // show only direct sandbox dependence entitlement for prod entitlement
      return data?.entitlementDetails?.sandboxEntitlements?.filter((sandboxEntitlementObj) => {
        const sandboxDependenceEntitlement = sandboxEntitlementObj?.relatesFromEntitlements?.find(
          (relationEntitlementObj) =>
            relationEntitlementObj?.relationshipType === RelationshipPathType.SANDBOX_DEPENDENCE &&
            relationEntitlementObj?.entitlementId === entitlementId
        );
        const hasSandboxOnSameTxa = isSandboxDecouplingEnabled
          ? sandboxEntitlementObj?.transactionAccountId === transactionAccountId
          : true;
        if (sandboxDependenceEntitlement && hasSandboxOnSameTxa) {
          return true;
        }
        return false;
      });
    }
    return data?.entitlementDetails.sandboxEntitlements;
  }, [data, currentEntitlementType, entitlementId]);

  const totalCount = sandboxEdges?.length || 0;

  const getRows = () => {
    return (sandboxEdges ?? []).map((sandboxNode, index) => {
      const appsCount = sandboxNode?.linkedAppsId?.length ?? 0;

      const isFutureDatedInactiveEntitlement =
        isFutureDatedEntitlementEnabled &&
        isFutureDatedInactiveSubscription(
          !!sandboxNode?.isFutureDatedSubscription,
          sandboxNode?.billingStatus as BillingStatus
        );

      const productDetailView = (
        <CloudProductDetailView
          productId={sandboxNode?.productId as string}
          entitlementNumber={sandboxNode?.entitlementNumber || ''}
          provisioningStatus={sandboxNode?.provisioningStatus}
          productName={sandboxNode?.productName}
          cloudSiteId={sandboxNode?.cloudSiteId}
          trial={sandboxNode?.trial ?? false}
          isApp={sandboxNode?.isApp ?? false}
          isSandbox={true}
          productLink={sandboxNode?.entitlementNumber ?? ''}
          version={version}
        />
      );

      const entitlementStatus = (
        <EntitlementStatus
          billingStatus={sandboxNode?.billingStatus}
          provisioningStatus={sandboxNode?.provisioningStatus}
          scheduledChanges={sandboxNode?.scheduledChanges}
          isFutureDatedInactiveEntitlement={isFutureDatedInactiveEntitlement}
          hideInactiveStatus={isFutureDatedInactiveEntitlement}
        />
      );

      const offering = (
        <>
          {sandboxNode?.edition ?? <NoData />}
          {version === 2 && (
            <Text size="small" color="color.text.subtlest" as="p">
              {capitalize(sandboxNode?.billingFrequency ?? '')}
            </Text>
          )}
        </>
      );

      const appCount = isDesignIteration1Enabled ? (
        <AppsCount
          version={2}
          subscription={{ id: sandboxNode?.entitlementId ?? '', linkedAppsId: sandboxNode?.linkedAppsId ?? [] }}
          baseRoute={subscriptionsRoutePPC}
        />
      ) : (
        <Link to={sandboxNode?.entitlementId ? `${subscriptionsRoutePPC}${sandboxNode?.entitlementId}#apps` : '#'}>
          <AppsCountCell isAppPresent={appsCount !== 0}> {appsCount}</AppsCountCell>
        </Link>
      );

      const cloudSiteLink = (
        <CloudSiteLink
          siteName={sandboxNode?.cloudSiteHostname}
          txaId={transactionAccountId}
          cloudSiteId={sandboxNode?.cloudSiteId}
        />
      );

      const billingProfile = (
        <BillingProfileNameFromId
          id={sandboxNode?.billingGroupId}
          txaId={transactionAccountId}
          showBillingProfileNumber
        />
      );

      const latestPeriodEndDate = sandboxNode?.latestPeriodEndDate ? (
        getLatestPeriodEndDate(sandboxNode?.billingStatus, sandboxNode?.latestPeriodEndDate)
      ) : (
        <NoData />
      );

      const cellsContent = (() => {
        return [
          {
            content: productDetailView,
          },
          {
            content: entitlementStatus,
          },
          {
            content: offering,
          },
          {
            content: appCount,
          },
          {
            content: cloudSiteLink,
          },
          {
            content: billingProfile,
          },
          {
            content: latestPeriodEndDate,
          },
        ];
      })();

      return {
        key: sandboxNode?.entitlementNumber ?? index.toString(),
        cells: cellsContent,
      };
    });
  };

  const getHead = () => {
    const cells = [
      {
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION,
        shouldTruncate: true,
        width: isDesignIteration1Enabled ? 15 : 25,
      },
      {
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.STATUS,
        width: isDesignIteration1Enabled ? 8 : 10,
      },
      {
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING,
        width: 10,
      },
      {
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.APPS,
        width: 10,
      },
      {
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.SITE,
        width: 25,
      },
      {
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_PROFILE,
        width: 15,
      },
      {
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_ON,
        width: 10,
      },
    ];
    return { cells };
  };

  return (
    <div className="statuspage-tab" data-testid="statuspage-sandbox">
      <DynamicResultTable
        testId="sandbox-entitlements"
        beforeTable={!isDesignIteration1Enabled ? <TableHeading>{tableHeading}</TableHeading> : <></>}
        head={getHead()}
        rows={getRows()}
        emptyStateProps={isDesignIteration1Enabled ? genericEmptyStateProp : emptyStateProp}
        isLoading={loading}
        errorStateProps={errorStateProp}
        error={error?.message}
        totalCount={totalCount}
      />
    </div>
  );
};
